import { Injectable } from '@angular/core';
import {
  ApiService,
  BenchmarkService,
  Device,
  DevicesService,
  IEntity,
  IEntityCache,
  InputDatastoreService,
  LoggerService,
  NotificationService,
  Site,
  SitesService,
  TimeMode,
  TimestampRange,
  UnmanagedDevicesService
} from '@nrwl/common-lib';
import {
  difference,
  each,
  extend,
  filter,
  find,
  findIndex,
  forEach,
  get,
  isArray,
  isString,
  merge,
  remove,
  sortBy
} from 'lodash';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { Monitor } from '../entities/monitor.model.factory';
import { Agent } from '../entities/agent.model.factory';
import { first, map, takeUntil } from 'rxjs/operators';
import { IAgentSelector, IMonitor } from '../entities/types';

@Injectable({
  providedIn: 'root'
})
export class MonitorsService implements IEntityCache {
  deltaTimeFrom: number;
  historicalmonitorcache: Array<Monitor>;
  realtimemonitorcache: Array<Monitor>;
  cache: BehaviorSubject<Array<Monitor>>;
  currentMode: TimeMode = 'real-time';
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  siteList: any;
  item: Monitor;
  pageSize: number;
  updated: Subject<Array<Monitor>>;
  queryLoading = false;
  overallMonitors: Array<Monitor> = [];
  monitorCountResponse: {
    success: {
      activeMonitorsCount: number;
      monitorMaxLimit: number;
    };
  };
  initialQuery = false;

  private readonly unsubscribe: Subject<void> = new Subject();
  private GetAllMonitorsURL = '/monitor/MonitorManager/v1/monitorListScroll/scroll?';
  private GetSingleMonitorURL = '/monitor/v2/monitors';
  private _isConnectionSuccessful: boolean;
  private notificationPostURL = '/notification/v1/operations/notifications/notification';
  private monitorMaxLimitUrl = '/monitor/MonitorManager/v1/monitor/activeMonitorsCountAndLimit';
  constructor(
    private readonly apiService: ApiService,
    private readonly devicesService: DevicesService,
    private readonly sitesService: SitesService,
    private readonly logging: LoggerService,
    private readonly notificationService: NotificationService,
    private readonly inputDatastoreService: InputDatastoreService,
    private readonly benchmarkService: BenchmarkService,
    private readonly unmanagedDeviceService: UnmanagedDevicesService
  ) {
    this.deltaTimeFrom = null;
    this.realtimemonitorcache = [];
    this.historicalmonitorcache = [];

    this.cache = new BehaviorSubject([]);
    this.pageSize = 5000;
    // GLILES: Doens't look like anyone is using siteList from this service.
    // Commenting out because it was causing 401 errors on login
    //this._getSites$();
  }

  getEntityName(): string {
    return 'monitor';
  }

  findById(id: string): IEntity {
    return this.getMonitorsById(id);
  }

  findByName(name: string): IEntity {
    return find(this.getMonitors(), monitor => monitor.getName() === name);
  }

  getByName(name: string): IEntity[] {
    return filter(this.getMonitors(), monitor => monitor.getName() === name);
  }

  findByEscapedName(name: string): IEntity {
    return find(this.getMonitors(), monitor => monitor.getEscapedName() === name);
  }

  getByEscapedName(name: string): IEntity[] {
    return filter(this.getMonitors(), monitor => monitor.getEscapedName() === name);
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValues(): any[] {
    return this.getMonitors();
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValuesAsync(): Observable<Array<any>> {
    /*eslint-enable*/
    const monitors = this.getValues();
    if (!monitors || monitors.length === 0) {
      return this.updateMonitors(this.realtimemonitorcache).pipe(first());
    }
    return of(monitors).pipe(first());
  }

  getMonitors(): Array<Monitor> {
    return this.chooseCacheModel(this.currentMode);
  }

  getMonitorsById(monitorId: string): Monitor {
    return find(this.getMonitors(), monitor => monitor.getId() == monitorId);
  }

  dispose(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getMonitorById$(monitorId: string): any {
    const url = `${this.GetSingleMonitorURL}/` + monitorId;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.get$<any>(url, {});
  }

  updateMonitors(
    cache: Array<Monitor>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params?: TimestampRange,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    firstRequest?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mode?: TimeMode,
    action?: string,
    scrollId?: string
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<any> {
    if (this.queryLoading) {
      return this.updated;
    }
    this.updated = new Subject();
    this.benchmarkService.registerPageEvent('monitorQuery');
    this.GetAllMonitorsURL = '/monitor/MonitorManager/v1/monitorListScroll/scroll?';
    if (scrollId) {
      this.GetAllMonitorsURL =
        this.GetAllMonitorsURL + 'queryId=' + scrollId + '&size=' + this.pageSize;
    } else {
      this.GetAllMonitorsURL = this.GetAllMonitorsURL + 'size=' + this.pageSize;
    }
    this.queryLoading = true;
    this.initialQuery = false;
    this.apiService
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      .get$<any>(this.GetAllMonitorsURL, {})
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.queryLoading = false;
          this.benchmarkService.logPageEventTime('Monitor query', 'monitorQuery');
          this.benchmarkService.registerPageEvent('dataManipulation');
          if (response && isArray(response.success.monitors)) {
            if (!scrollId) {
              // First api call clear the monitors array
              this.overallMonitors = [];
            }
            if (cache.length === 0) {
              this.initialQuery = true;
            }
            const responseData = this.transformResponse(response.success.monitors);
            this.overallMonitors.push(...responseData);
            this.assignMonitorToCacheList(cache);
            this.assignSiteAndAgent(cache);
            this._isConnectionSuccessful = true;
            this.updated.next(this.getMonitors());
            this.benchmarkService.logPageEventTime('Monitor transforming data', 'dataManipulation');
            this.paginateMonitors(response.success.monitors.length, response.success.scrollId);
          }
        },
        // eslint-disable-next-line
        error => {
          this.queryLoading = false;
        },
        () => {
          this._isConnectionSuccessful = false;
          this.updated.complete();
          this.queryLoading = false;
        }
      );
    return this.updated;
  }

  paginateMonitors(count: number, scrollId: string): void {
    if (count === this.pageSize) {
      if (this.initialQuery) {
        //Update the cache immediately when first response came back
        this.cache.next(this.chooseCacheModel(this.currentMode));
      }
      this.updateMonitors(this.getMonitors(), null, null, null, 'pushCache', scrollId);
    } else {
      //update the cache once all scroll api completed
      this.cache.next(this.chooseCacheModel(this.currentMode));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  transformResponse(dataIn: any): Array<Monitor> {
    dataIn = isString(dataIn) ? JSON.parse(dataIn) : dataIn;
    const response = [];
    if (dataIn !== null && isArray(dataIn)) {
      dataIn.forEach(result => {
        const configIn = <IMonitor>{};
        extend(configIn, result);
        merge(configIn, this.populateAgentLabels(configIn));
        const monitorObj: Monitor = new Monitor(configIn);
        response.push(monitorObj);
      });
    }
    return response;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  assignSiteAndAgent(cache: any): void {
    let deviceList = this._getDevice$();
    const unamangedDeviceList = this._getUnManagedDevice$();
    deviceList = deviceList.concat(unamangedDeviceList);
    this.sitesService
      .getValuesAsync()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(sitesList => {
        const siteItems = this.mapIdWithObject(sitesList);
        const deviceItems = this.mapIdWithObject(deviceList);
        for (const monitor of cache) {
          this.assignAgent(deviceItems, monitor);
          this.assignSite(siteItems, monitor);
        }
      });
  }

  //eslint-disable-next-line
  mapIdWithObject(listItem: any): any {
    const mappedObject = {};
    for (const item of listItem) {
      mappedObject[item.getId()] = item;
    }
    return mappedObject;
  }

  //eslint-disable-next-line
  assignAgent(deviceItems: any, monitor: Monitor): void {
    const originator: Device = deviceItems[monitor.getAgentId()];
    const destination: Device = deviceItems[monitor.getDestinationAgentId()];
    if (originator) {
      monitor.setAgent(new Agent(originator.getDevice()));
    }
    if (destination) {
      monitor.setDestination(new Agent(destination.getDevice()));
    }
  }

  //eslint-disable-next-line
  assignSite(sitesItems: any, monitorObj: Monitor): void {
    if (sitesItems) {
      const originator: Site = sitesItems[monitorObj.getOriginatorSiteId()];
      const destination: Site = sitesItems[monitorObj.getDestinationSiteId()];
      if (originator) {
        monitorObj.setOriginatorSiteName(originator.getName());
      }
      if (destination) {
        monitorObj.setDestinationSiteName(destination.getName());
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  populateAgentLabels(configIn: any): any {
    const labelsInfo = get(configIn, 'labels', null);
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {};
    if (labelsInfo && labelsInfo.length > 0) {
      forEach(labelsInfo, label => {
        const keyValue = label.split(':');
        result[keyValue[0].trim()] = Number(keyValue[1].trim());
      });
    }
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  getTypeById(typeList: any, id: any): any {
    for (const item of typeList) {
      if (item.getId() === id) {
        return item;
      }
    }
  }

  getMonitorsByApp(appId: number): Observable<Array<Monitor>> {
    return this.getValuesAsync().pipe(
      map((monitors: Monitor[]) => monitors.filter(m => m.isAppDepended(appId)))
    );
  }

  chooseCacheModel(mode?: TimeMode): Array<Monitor> {
    if (mode === 'historical') {
      return this.historicalmonitorcache;
    } else {
      return this.realtimemonitorcache;
    }
  }

  assignMonitorToCacheList(cache: Array<Monitor>): void {
    // simplified algo, that simply replaces full cache with new list
    // notice we preserve the array object!
    cache.length = 0;
    for (const entry of this.overallMonitors) {
      cache.push(entry);
    }
  }

  findIndexById(cache: Array<Monitor>, uid: string): number {
    return findIndex(cache, monitor => uid === monitor.getId());
  }

  setcountResponse(response: {
    success: {
      activeMonitorsCount: number;
      monitorMaxLimit: number;
    };
  }): void {
    this.monitorCountResponse = response;
  }

  getCountResponse(): {
    success: {
      activeMonitorsCount: number;
      monitorMaxLimit: number;
    };
  } {
    return this.monitorCountResponse;
  }
  findMonitorByID(monitorId: string): Monitor {
    for (const monitor of this.getMonitors()) {
      if (monitor.getId() === monitorId) {
        return monitor;
      }
    }
  }

  findMonitorCacheByID(cache: Array<Monitor>, monitorId: string): Monitor {
    for (const monitor of cache) {
      if (monitor.getId() === monitorId) {
        return monitor;
      }
    }
  }

  /**
   * Remove monitor from cache
   * @param cache
   * @param monitorId
   * @returns {Array} of monitors that left after removing
   */
  removeMonitorCacheByID(cache: Array<Monitor>, monitorId: string): Array<Monitor> {
    return remove(cache, monitor => monitor.getId() === monitorId);
  }

  findMonitorByIndex(cache: Array<Monitor>, index: number): Monitor {
    return get(cache, `[${index}]`, null);
  }

  clearRealtimeCache(): void {
    this.realtimemonitorcache.length = 0;
  }

  clearHistoricalCache(): void {
    this.historicalmonitorcache.length = 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  removeMonitor$(url: string, data: any): Observable<any> {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.delete$<any>(url, data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateMonitorState$(url: string, data: any): Observable<any> {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.post$<any>(url, data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  removeMonitorsResponse(response: any, data: any): void {
    const result: Array<string> = response.success;
    if (result.length > 0) {
      //backend returns a list of non removed monitors
      this.logging.info(`Monitor has return unknown result with params ${data}`);
    } else {
      const monitorsToRemove = difference(data, result);
      this.removeMonitors(monitorsToRemove);
    }
  }

  removeMonitors(data: Array<string>): void {
    const allMonitors = this.getMonitors();
    each(data, id => {
      this.removeMonitorCacheByID(allMonitors, id);
    });

    this.cache.next(this.chooseCacheModel(this.currentMode));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateMonitorStopResponse(response: any, data: any): void {
    this.logging.info(`Monitor was stopped: ${JSON.stringify(response)}`);
    const result = response.success;
    if (result.length > 0) {
      result.forEach(data => {
        data.operationalState = 'stopping';
        data.operationalStateMessage = 'Stopping the monitor';
      });
      this.updateMonitorCacheAfterAction(result);
      this.notificationService.onMonitorStopStateChange(result);
    } else {
      this.logging.info(`Monitor has return unknown result with params ${data}`);
    }
  }

  getWarningMessage(testInfo: { maxLimit: number; activeCount: number }): string {
    return `There are ${testInfo.activeCount} tests currently enabled. Adding new tests will exceed the ${testInfo.maxLimit} maximum tests limit. Please stop or remove some tests to allow new tests to run.`;
  }

  getCreateTestWarningMsg(limit: number): {
    id: string;
    alternateMsg: string;
    alternateTitle: string;
  } {
    const param = {
      id: 'start.activeTest',
      alternateMsg: `You have reached ${limit} tests which is the maximum number of test that can be created. Please stop or remove some tests to allow new tests to run.`,
      alternateTitle: 'Test count limit exceeded'
    };
    return param;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  updateMonitorStartResponse(response: any, data: any): void {
    this.logging.info(`Monitor was started: ${JSON.stringify(response)}`);
    const result = response.success;
    if (result.length > 0) {
      result.forEach(data => {
        data.operationalState = 'starting';
        data.operationalStateMessage = 'Starting the monitor';
      });
      this.updateMonitorCacheAfterAction(result);
      this.notificationService.onMonitorStartStateChange(result);
    } else {
      this.logging.info(`Monitor has return unknown result with params ${data}`);
    }
  }

  updateMonitorCacheAfterAction(data: Array<IMonitor>): void {
    this.updateMonitorCache(this.getMonitors(), data);
  }

  updateMonitorCache(cache: Array<Monitor>, data: Array<IMonitor>): void {
    each(data, item => {
      const index = this.findIndexById(cache, item.id);
      if (index !== -1) {
        this.setMonitorParameters(cache, item, index);
      }
    });

    this.cache.next(this.chooseCacheModel(this.currentMode));
  }

  setMonitorParameters(cache: Array<Monitor>, item: IMonitor, index: number): void {
    if (item.operationalState) {
      this.findMonitorByIndex(cache, index).setOperationalState(item.operationalState);
      this.findMonitorByIndex(cache, index).setStateMessage(item.operationalStateMessage);
      this.findMonitorByIndex(cache, index).setAdministrativeState(item.administrativeState);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  sortDataByName(data: any): any {
    return sortBy(data, monitor => monitor.getName().toLowerCase().trim());
  }

  // Clear each page widget component queue
  clearQueues(): void {
    this.inputDatastoreService.clearQueues();
  }

  addMonitorResponse(): void {
    this.updateMonitors(this.getMonitors());
  }

  updateDataSourceMonitorItem(monitorItem: Monitor): void {
    this.item = monitorItem;
  }

  getDataSourceMonitorItem(): Monitor {
    return this.item;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  _getNotificationData$(data: any): Observable<any> {
    return this.apiService.post$<any>(this.notificationPostURL, data); //eslint-disable-line
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getMonitorMaxLimit$(): any {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.apiService.get$<any>(this.monitorMaxLimitUrl, {});
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatTruespeedEditData(mData: IMonitor): any {
    const testConfigData = mData.testConfig;
    const testConfig = JSON.parse(testConfigData).config[0].primaryConfig['ietf-lmap-control:lmap'];
    const scheduleEvent = testConfig.events.event[0].periodic;
    const testdata = testConfig.schedules.schedule[0].action[0].option[0];
    const reqData = JSON.parse(testdata.value)['truespeed:truespeed']['truespeed-parameters'];
    let data = {};
    const masterData = reqData;
    data = {
      id: mData['id'],
      runOnDemand: mData.runType,
      srcSiteId: mData['src-site-id'],
      dstSiteId: mData['dst-site-id'],
      upstream: masterData['engine-b-to-a-cir'],
      downstream: masterData['engine-a-to-b-cir'],
      editMode: true,
      scheduler: scheduleEvent,
      originatorSite: this.getUpdatedSiteInfo(mData.agentSelector),
      destinationSite: this.getUpdatedSiteInfo(mData.service.agentSelector),
      testName: mData.alias,
      qosType: masterData['tos-dscp'].type,
      tcpPort: masterData['tcp-port'],
      qosVal: masterData['tos-dscp']['dscp']?.toString()
        ? masterData['tos-dscp']['dscp']?.toString()
        : '0',
      cirStart: masterData['percent-start-window-walk'],
      tosValue: masterData['tos-dscp']['tos']?.toString()
        ? masterData['tos-dscp']['tos']?.toString()
        : '0',
      noOfwalk: masterData['num-windows'].toString(),
      duration: masterData['window-duration'],
      noOfConnections: masterData.connections.toString(),
      connections: masterData['connections'] > 0 ? 'manual' : 'auto',
      checkDynamic: masterData['dynamic-saturation'].toString(),
      enable: masterData['run-saturation-window'].toString(),
      boostWindow: masterData['percent-over-saturate-window']
        ? masterData['percent-over-saturate-window']
        : 20,
      runOnAgent: masterData['run-exclusive'],
      boostConnection: masterData['percent-over-saturate-connections']
        ? masterData['percent-over-saturate-connections']
        : 40
    };
    return data;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatServiceEditData(mData: IMonitor): any {
    let data = {};
    data = {
      id: mData['id'],
      deviceData: mData.agentSelector,
      selectedAppData: mData.service,
      proxyConfig:
        mData?.metaData !== null
          ? mData?.metaData
          : { config: { useDefault: true, protocol: null, address: null, port: null } },
      runOnDemand: mData.runType === 'on-going' ? 'never' : '8hr',
      editMode: true,
      testName: mData.alias,
      testType: mData.testType,
      originatorSite: mData.originatorSite
    };
    return data;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatMultistepEditData(mData: IMonitor): any {
    const testConfig = JSON.parse(mData.testConfig).config[0].primaryConfig[
      'ietf-lmap-control:lmap'
    ].schedules.schedule[0].action[0].option;
    const multistepConfig = JSON.parse(testConfig.find(item => item.id === 'multistep').value);
    const scriptUrl = multistepConfig.multistep.config.pagewalk['script-url'] ?? null;
    const interval = multistepConfig.multistep.config.pagewalk.interval;
    const scriptName = scriptUrl ? /[^/]*$/.exec(scriptUrl)[0] : null;
    let data = {};
    data = {
      id: mData['id'],
      deviceData: mData.agentSelector,
      selectedAppData: mData.service,
      proxyConfig:
        mData?.metaData !== null
          ? mData?.metaData
          : { config: { useDefault: true, protocol: null, address: null, port: null } },
      runOnDemand: mData.runType,
      editMode: true,
      testName: mData.alias,
      testType: mData.testType,
      originatorSite: mData.originatorSite,
      scriptName: scriptName,
      interval: interval
    };
    return data;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatVoipEditData(mData: IMonitor): any {
    let data = {};
    data = {
      testName: mData.alias,
      runOnDemand: mData.runType === 'on-going' ? 'never' : '8hr',
      editMode: true,
      testType: mData.testType,
      metaData: mData.metaData,
      monitorId: mData.id,
      destinationInfo: {
        label: mData.service.agentSelector?.alias,
        title: mData.destinationSite,
        destSiteId: mData['dst-site-id'],
        phonenumber: mData.service.phonenumber,
        data: mData.service
      },
      originatorInfo: {
        label: mData.agentSelector.alias,
        title: mData.originatorSite,
        srcSiteId: mData['src-site-id'],
        data: mData.agentSelector
      }
    };
    return data;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatNetworkTestEditData(mData: IMonitor): any {
    let data = {};
    data = {
      testName: mData.alias,
      runOnDemand: mData.runType === 'on-going',
      editMode: true,
      testType: mData.testType,
      metaData: mData.metaData,
      monitorId: mData.id,
      originator: mData.agentSelector,
      destination: mData.service,
      destinationInfo: {
        label: mData.service.agentSelector?.alias,
        title: mData.destinationSite,
        destSiteId: mData['dst-site-id'],
        phonenumber: null,
        data: mData.service
      },
      originatorInfo: {
        label: mData.agentSelector.alias,
        title: mData.originatorSite,
        srcSiteId: mData['src-site-id'],
        data: mData.agentSelector
      }
    };
    return data;
  }
  private getUpdatedSiteInfo(data: IAgentSelector): IAgentSelector {
    if (data?.id) {
      const deviceData: IEntity = this.devicesService.findById(data.id);
      data.alias = deviceData?.getEscapedName();
    }
    return data;
  }

  private _getSites$() {
    this.sitesService
      .getValuesAsync()
      .pipe(first())
      .subscribe(sites => {
        this.siteList = sites;
      });
  }

  private _getDevice$() {
    return this.devicesService.getValues();
  }
  private _getUnManagedDevice$() {
    return this.unmanagedDeviceService.getValues();
  }
}
